"use client";
import { HighlightMenu, MenuButton } from "react-highlight-menu";
import { BotIcon } from "./icons";
import useChat from "@/hooks/useChat";
import { useMutation } from "@tanstack/react-query";
import { ChatMessage, GetBotResponseProps } from "@/types";
import { agenticGetBotResponse } from "@/lib/api-client";
import { v4 as uuid } from "uuid";
import { useParams } from "next/navigation";

export default function HighlightText() {
  const {
    handleSelectedText,
    handleAddChat,
    chatStartedFn,
    chatEndFn,
    activeChatId,
  } = useChat();
  const { chat_id } = useParams();

  const { mutate: getBotResponse } = useMutation({
    mutationFn: async (data: GetBotResponseProps) => {
      return agenticGetBotResponse(data);
    },
    onSuccess: async (data) => {
      const { result, sql_query, table_data } = data;

      const chatMessage = {
        id: result?.id ?? uuid(),
        role: result?.role ?? "assistant",
        description: result?.description ?? "",
        chart_data: result?.chart_data,
        chart_type: result?.chart_type,
        title: result?.title,
        type: result?.type,
        value: result?.value,
        x_axis: result?.x_axis,
        y_axis: result?.y_axis,
        sql_query: sql_query ?? result?.sql_query_kpi,
        table_data: table_data,
        agentic_desc: result?.agentic_desc,
        response_type: result?.response_type,
      };

      handleAddChat(chatMessage as ChatMessage);
    },
    onSettled: () => {
      chatEndFn();
    },
  });

  const handleTextSelection = (selectedText: string) => {
    if (!selectedText.trim()) {
      return;
    }

    chatStartedFn();

    // Add user message to chat
    const userMsg = {
      id: uuid(),
      role: "user",
      description: `Give me more information about - ${selectedText}`,
    };
    handleAddChat(userMsg);

    // Make API call to get bot response
    const payload = {
      user_prompt: `Give me more information about - ${selectedText}`,
      chat_id: (chat_id as string) || activeChatId || "",
      isRetry: 0,
      isGlobalSearch: 1,
    };

    getBotResponse(payload);
  };

  return (
    <HighlightMenu
      target=".ask-trx"
      allowedPlacements={["top", "bottom"]}
      menu={({ selectedText }) => (
        <MenuButton
          onClick={() => {
            if (selectedText) {
              handleTextSelection(selectedText);
            }
          }}
        >
          <div className="flex items-center justify-center gap-2">
            <div className="rounded-full h-8 w-8 bg-gradient-to-br from-orange-500 to-orange-600/80 text-primary-foreground hover:from-orange-400/70 hover:to-orange-600 transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105 hover:rotate-12 group flex items-center justify-center">
              <BotIcon className="w-6 h-6 group-hover:text-secondary transition-colors duration-300 text-white" />
            </div>
            <p className="text-secondary font-semibold">Ask TRx</p>
          </div>
        </MenuButton>
      )}
    />
  );
}
